import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ICellRendererParams } from 'ag-grid-enterprise';
import * as Bixby from '$/core';
import InputBlock from '$/components/global/inputBlock';
import { CompanyService } from '$/services';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import Loading from '$/components/loading/Loading';
import './PackageIdLookupModal.scss';
import { FormikErrors } from 'formik';
import { CompanyProfileValues } from '$/pages/admin/company/company-detail/CompanyProfile';

export interface IPackageIdLookupModalProps {
  currentCompany: Bixby.Models.AdminCompany;
  lfiDealId?: string | number;
  setPackageId: (packageId: number) => Promise<void | FormikErrors<CompanyProfileValues>>;
  setDealId: (dealId: number) => Promise<void | FormikErrors<CompanyProfileValues>>;
  dispatchToastError: (message: string) => void;
  showPackageIdLookupModal: boolean;
  setShowPackageIdLookupModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const parseDealId = (dealId?: string | number | null) => {
  const trimmedDealId = dealId?.toString().trim();
  const parsedDealId = Number(trimmedDealId);

  return trimmedDealId && Number.isFinite(parsedDealId) ? parsedDealId : undefined;
};

export const PackageIdLookupModal: React.FC<IPackageIdLookupModalProps> = ({
  currentCompany,
  lfiDealId,
  setPackageId,
  setDealId,
  dispatchToastError,
  showPackageIdLookupModal,
  setShowPackageIdLookupModal,
}) => {
  const dealIdRef = useRef<HTMLInputElement>(null);
  const [packageIdLookupResult, setPackageIdLookupResult] = useState<
    Bixby.Models.CreditSightsPackageIdLookupResponse[] | null
  >(null);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
  const [dealIdError, setDealIdError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const companyDealId = parseDealId(lfiDealId);

  const fetchPackageIdLookup = useCallback(
    async (manualDealId?: number) => {
      const requestDealId = companyDealId ?? manualDealId;
      const requestCompanyId = requestDealId ? undefined : currentCompany.creditSightsCompanyId ?? undefined;

      if (!requestDealId && !requestCompanyId) {
        setDealIdError('LFI Deal Id is required.');
        return;
      }

      setDealIdError(undefined);
      setLoading(true);

      try {
        const request: Bixby.Models.CreditSightsPackageIdLookupRequest = requestDealId
          ? {
              lFIDealId: requestDealId,
            }
          : {
              cSCompanyId: requestCompanyId,
            };

        const result = await CompanyService.getPackageIdLookupAsync(request);

        setPackageIdLookupResult(result);
      } catch (e: any) {
        setPackageIdLookupResult(null);
        dispatchToastError(
          Bixby.API.getErrorMessage(e) || 'Unable to fetch package information. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    },
    [companyDealId, currentCompany.creditSightsCompanyId, dispatchToastError]
  );

  useEffect(() => {
    if (showPackageIdLookupModal && (companyDealId || currentCompany.creditSightsCompanyId)) {
      fetchPackageIdLookup();
    }
  }, [showPackageIdLookupModal, companyDealId, currentCompany.creditSightsCompanyId, fetchPackageIdLookup]);

  const handleClose = useCallback(() => {
    setSelectedPackageId(null);
    setSelectedDealId(null);
    setPackageIdLookupResult(null);
    setDealIdError(undefined);
    setSaving(false);
    setShowPackageIdLookupModal(false);
  }, [setShowPackageIdLookupModal]);

  const handleSelectPackageId = useCallback(async () => {
    if (!selectedPackageId) {
      return;
    }

    try {
      setSaving(true);
      await setPackageId(selectedPackageId);
      if (selectedDealId !== null) {
        await setDealId(selectedDealId);
      }
      handleClose();
    } catch (e: any) {
      setSaving(false);
      dispatchToastError(
        Bixby.API.getErrorMessage(e) || 'Unable to apply the selected Package ID. Please try again later.'
      );
    }
  }, [selectedPackageId, selectedDealId, setDealId, setPackageId, handleClose, dispatchToastError]);

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }
    return ['copy', 'copyWithHeaders', 'separator', 'export'];
  };

  const RadioRenderer = (props: ICellRendererParams<Bixby.Models.CreditSightsPackageIdLookupResponse>) => {
    const { data } = props;
    const dealId = data?.dealId ?? null;

    return (
      <input
        type="radio"
        name="packageIdSelection"
        checked={selectedPackageId === data?.packageId}
        disabled={!data}
        onChange={() => {
          if (!data) {
            return;
          }

          setSelectedPackageId(data.packageId);
          setSelectedDealId(dealId);
        }}
      />
    );
  };

  return showPackageIdLookupModal ? (
    <Modal
      active={true}
      wideView={true}
      className="packageid_lookup_modal"
      title="LFI Package ID Lookup"
      subtitle={
        lfiDealId
          ? `Using LFI Deal Id: ${lfiDealId}`
          : currentCompany.creditSightsCompanyId
          ? `Using CreditSights Id: ${currentCompany.creditSightsCompanyId}`
          : undefined
      }
      onClose={handleClose}>
      <div className="select_packageid_modal">
        {loading && <Loading />}
        {packageIdLookupResult && packageIdLookupResult.length > 0 && !loading ? (
          <div className="packageId_lookup_data_grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  enableRangeSelection={true}
                  getContextMenuItems={getContextMenuItems}
                  domLayout="autoHeight"
                  defaultColDef={{
                    flex: 1,
                    minWidth: 150,
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    cellClass: 'align-left',
                  }}
                  rowData={packageIdLookupResult}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  stopEditingWhenCellsLoseFocus={true}
                  headerHeight={35}
                  columnDefs={[
                    {
                      headerName: '',
                      minWidth: 50,
                      cellRenderer: RadioRenderer,
                      sortable: false,
                      filter: false,
                    },
                    {
                      field: 'borrowerName',
                      headerName: 'Borrower Name',
                    },
                    {
                      field: 'packageName',
                      headerName: 'Package Name',
                    },
                    {
                      field: 'commitmentDate',
                      headerName: 'Commitment Date',
                    },
                    {
                      field: 'leadArranger',
                      headerName: 'Lead Arranger',
                    },
                    {
                      field: 'amountUsd',
                      headerName: 'Amount',
                    },
                    {
                      field: 'type',
                      headerName: 'Type',
                    },
                    {
                      field: 'dealId',
                      headerName: 'LFI Deal Id',
                      valueGetter: params => params.data?.dealId,
                    },
                    {
                      field: 'packageId',
                      headerName: 'LFI Package Id',
                    },
                    {
                      field: 'createdDate',
                      headerName: 'Created Date',
                    },
                  ]}
                />
              </div>
            </div>
            <div className="modal_button_container">
              <BixButton
                type="button"
                useLoading
                isLoading={saving}
                disabled={saving || !selectedPackageId}
                onClick={handleSelectPackageId}>
                Select
              </BixButton>
              <BixButton light type="button" disabled={saving} onClick={handleClose}>
                Cancel
              </BixButton>
            </div>
          </div>
        ) : (
          <>
            {!lfiDealId && !currentCompany.creditSightsCompanyId && !loading && (
              <div>
                <InputBlock title="LFI Deal Id">
                  <input type="text" ref={dealIdRef} name="lfiDealId" disabled={loading} />
                  {dealIdError && <div className="error error-message">{dealIdError}</div>}
                </InputBlock>
                <div className="modal_button_lookup_container">
                  <BixButton
                    type="button"
                    useLoading
                    isLoading={loading}
                    disabled={loading}
                    onClick={() => fetchPackageIdLookup(parseDealId(dealIdRef.current?.value))}>
                    Lookup
                  </BixButton>
                  <BixButton light type="button" disabled={loading} onClick={handleClose}>
                    Cancel
                  </BixButton>
                </div>
              </div>
            )}
          </>
        )}
        {packageIdLookupResult && packageIdLookupResult.length === 0 && !loading && <div>No results found.</div>}
      </div>
    </Modal>
  ) : (
    <></>
  );
};
